import type mapboxgl from "mapbox-gl";

/**
 * Switching the Mapbox base style (`map.setStyle`) throws away every source and
 * layer on the map, including the ones the trial design draws — plots, strips,
 * AB lines, machinery grid lines, buffer zones, autozones and soil imagery.
 *
 * Rather than making every add-layer helper in the codebase idempotent, we
 * snapshot the custom part of the style before the switch and replay it once the
 * new style has loaded.
 *
 * The Mapbox base styles only ever use `vector`, `raster` and `raster-dem`
 * sources, so anything of another type was added by this application.
 */

/** Source types the application adds; everything else belongs to the base style. */
const CUSTOM_SOURCE_TYPES = new Set(["geojson", "image", "video", "canvas"]);

export interface CustomMapState {
  sources: Array<{ id: string; source: mapboxgl.AnySourceData }>;
  layers: mapboxgl.AnyLayer[];
}

const isCustomSource = (source: mapboxgl.AnySourceData): boolean =>
  CUSTOM_SOURCE_TYPES.has((source as { type?: string })?.type ?? "");

/**
 * Snapshot every application-owned source and layer currently on the map.
 * Returns empty collections when the style is not readable yet.
 */
export const captureCustomMapState = (
  map: mapboxgl.Map | null
): CustomMapState => {
  const empty: CustomMapState = { sources: [], layers: [] };
  if (!map) {
    return empty;
  }

  let style: mapboxgl.Style | undefined;
  try {
    style = map.getStyle();
  } catch (error) {
    console.warn("Could not read map style before base layer switch:", error);
    return empty;
  }

  if (!style?.sources) {
    return empty;
  }

  const sources = Object.entries(style.sources)
    .filter(([, source]) => isCustomSource(source))
    .map(([id, source]) => ({
      id,
      // Clone so later mutations of the live style cannot corrupt the snapshot.
      source: structuredClone(source),
    }));

  const customSourceIds = new Set(sources.map(({ id }) => id));

  const layers = (style.layers ?? [])
    .filter((layer) => {
      const sourceId = (layer as { source?: unknown }).source;
      return typeof sourceId === "string" && customSourceIds.has(sourceId);
    })
    .map((layer) => structuredClone(layer));

  return { sources, layers };
};

/**
 * Id of the bottom-most application-owned layer, i.e. the first layer that is
 * not part of the Mapbox base style. Used as a `beforeId` so base-style layers
 * such as road labels can be pushed underneath the trial design.
 */
export const getFirstCustomLayerId = (
  map: mapboxgl.Map | null
): string | undefined => {
  if (!map) {
    return undefined;
  }

  let style: mapboxgl.Style | undefined;
  try {
    style = map.getStyle();
  } catch {
    return undefined;
  }

  if (!style?.sources || !style.layers) {
    return undefined;
  }

  const customSourceIds = new Set(
    Object.entries(style.sources)
      .filter(([, source]) => isCustomSource(source))
      .map(([id]) => id)
  );

  return style.layers.find((layer) => {
    const sourceId = (layer as { source?: unknown }).source;
    return typeof sourceId === "string" && customSourceIds.has(sourceId);
  })?.id;
};

/**
 * Replay a snapshot onto the map. Sources and layers that already exist are
 * skipped, and layers are re-added in their captured order so their relative
 * stacking survives the switch.
 */
export const restoreCustomMapState = (
  map: mapboxgl.Map | null,
  state: CustomMapState
): void => {
  if (!map || (!state.sources.length && !state.layers.length)) {
    return;
  }

  state.sources.forEach(({ id, source }) => {
    try {
      if (!map.getSource(id)) {
        map.addSource(id, source);
      }
    } catch (error) {
      console.warn(`Could not restore map source "${id}":`, error);
    }
  });

  state.layers.forEach((layer) => {
    try {
      if (!map.getLayer(layer.id)) {
        map.addLayer(layer);
      }
    } catch (error) {
      console.warn(`Could not restore map layer "${layer.id}":`, error);
    }
  });
};

/**
 * Switch the base style while preserving the application's own sources and
 * layers. Resolves the restore on the next `style.load`.
 */
export const setBaseStylePreservingLayers = (
  map: mapboxgl.Map | null,
  style: string
): void => {
  if (!map) {
    return;
  }

  const snapshot = captureCustomMapState(map);

  map.once("style.load", () => {
    restoreCustomMapState(map, snapshot);
  });

  map.setStyle(style);
};
